import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { login, password, registerDueDiligence, registerRegisterFirm, registerSearchFirms } from 'routes/routePaths';
import Navigation from '../Form/Navigation/Navigation';
import { HorizontalRuler, Section } from 'screens/BrokerPortal/screens/Registration/components/UI';
import {
  ACCOUNT_ROLE,
  FIELD,
  Form,
  USER_SOURCE_LABEL,
  USER_SOURCE_VALUE,
} from 'screens/BrokerPortal/screens/Registration/components/PersonalDetailsStep/types';
import { FormInputWrapper, PhoneInput, TextInput } from 'form/infrastructure/reactHookForm';
import { CUSTOM_ERROR, validationSchema } from './validationSchema';
import RadioGroup from 'form/infrastructure/reactHookForm/RadioGroup';
import { ADD_NEW_FIRM_OPTION } from 'screens/BrokerPortal/screens/Registration/components/SearchFirmsStep/SearchFirmsStep';
import useYupValidationResolver from 'form/yup/reactHookFormResolver';
import RegisterErrorModal from 'screens/BrokerPortal/screens/Registration/components/PersonalDetailsStep/RegisterErrorModal/RegisterErrorModal';
import { Nullable } from 'utils/types';
import { State } from 'screens/BrokerPortal/screens/Registration/store/types';
import { InputError } from 'form/components/InputWrapper/InputWrapper.styled';
import { InputWrapper, TextInput as CleanTextInput } from 'form/components';
import { Icon } from 'lmc-ui-library';
import { Heading, Text } from 'lmc-ui-library-rebrand';
import { Firm } from 'screens/BrokerPortal/screens/Registration/api';
import { Step } from 'screens/BrokerPortal/screens/Registration/steps';
import canViewPersonalDetailisStep from 'screens/BrokerPortal/screens/Registration/guard/canViewPersonalDetailisStep';
import { FIRM_TYPE } from '../RegisterFirmStep/types';
import BoxWrapper from '../Form/RegistrationFormTemplate/BoxWrapper/BoxWrapper';

interface Props {
  form: State['personalDetails']['form'];
  searchFirmForm: State['searchFirms']['form'];
  registerFirmForm: State['registerFirm']['form'];
  setPersonalDetailsForm: (data: Form) => void;
  selectedFirmId: Nullable<number>;
  register: () => Promise<{ token: string }>;
  registerLoading: State['personalDetails']['registerLoading'];
  setCurrentStep: (step: Step) => void;
  firmContext: Nullable<Firm>;
  clearRegistration: () => void;
}

const PersonalDetailsStep: React.FC<Props> = ({
  form,
  searchFirmForm,
  registerFirmForm,
  setPersonalDetailsForm,
  selectedFirmId,
  register,
  registerLoading,
  firmContext,
  setCurrentStep,
  clearRegistration,
}) => {
  const history = useHistory();
  const methods = useForm<Form>({
    mode: 'onBlur',
    defaultValues: form,
    resolver: useYupValidationResolver(validationSchema()),
  });
  const {
    handleSubmit,
    trigger,
    watch,
    setValue,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { isValid },
  } = methods;
  const watchUserSource = watch(FIELD.HOW_DID_USER_HEAR_ABOUT_LIVEMORE);

  const [modalError, setModalError] = useState<Nullable<Error>>(null);

  useEffect(() => {
    if (!canViewPersonalDetailisStep(searchFirmForm)) {
      history.push(registerSearchFirms);
    }
  }, [history, searchFirmForm]);

  useEffect(() => {
    setCurrentStep(Step.PERSONAL_DETAILS);
  }, [setCurrentStep]);

  const onSubmit = handleSubmit(async (data) => {
    setPersonalDetailsForm(data);
    try {
      const { token } = await register();
      history.push(`/bp/register/verify/${token}`);
      clearRegistration();
    } catch (e) {
      setModalError(e);
    }
  });

  useEffect(() => {
    setValue(FIELD.OPTIONAL_DETAILS, null);
  }, [watchUserSource, setValue]);

  const handleBack = () =>
    history.push(
      selectedFirmId === ADD_NEW_FIRM_OPTION
        ? registerFirmForm.firmType === FIRM_TYPE.DA
          ? registerDueDiligence
          : registerRegisterFirm
        : registerSearchFirms,
    );

  const handleContinue = async () => {
    await trigger();
  };

  return (
    <FormProvider<Form> {...methods}>
      <form onSubmit={onSubmit}>
        <BoxWrapper>
          <Heading mode="title" weight="bold">
            Personal details
          </Heading>
          <Text>We require this information to set up your account</Text>

          <Section>
            {firmContext && (
              <>
                <InputWrapper
                  newUi
                  name=""
                  title="Firm"
                  extraContent={[
                    {
                      content: (
                        <span style={{ color: 'var(--color-indigo-t11' }}>
                          Not your firm?{' '}
                          <Link color="var(--color-coral-rb)" to={registerSearchFirms}>
                            Please search again here
                          </Link>
                        </span>
                      ),
                      position: 'bottom',
                      variant: 'text',
                    },
                  ]}>
                  <CleanTextInput
                    newUi
                    disabled
                    value={`${firmContext.fcaNumber} - ${firmContext.name}`}
                    leftIcon={() => <Icon type="lock" color="var(--color-indigo-t5)" />}
                  />
                </InputWrapper>
                <HorizontalRuler newUi />
              </>
            )}

            <FormInputWrapper title="First name" name={FIELD.FIRST_NAME} newUi>
              <TextInput newUi />
            </FormInputWrapper>

            <FormInputWrapper title="Surname" name={FIELD.SURNAME} newUi>
              <TextInput newUi />
            </FormInputWrapper>

            <FormInputWrapper
              newUi
              title="Email address"
              name={FIELD.EMAIL}
              customErrorMessages={[
                {
                  type: CUSTOM_ERROR.EMAIL_DUPLICATE,
                  render: () => (
                    <InputError>
                      An account already exists using this email address. If this is your email address, please{' '}
                      <Link to={login}>log in</Link>, or <Link to={password.forgot}>reset your password</Link> if you
                      have forgotten it. Alternatively, please change your email address
                    </InputError>
                  ),
                },
              ]}>
              <TextInput newUi />
            </FormInputWrapper>

            <FormInputWrapper title="Contact number" name={FIELD.CONTACT_NUMBER} newUi>
              <PhoneInput newUi />
            </FormInputWrapper>
          </Section>

          <HorizontalRuler newUi />

          <Section>
            <Heading mode="heading" weight="bold" style={{ marginBottom: '2rem' }}>
              Your role
            </Heading>
            <Text color="var(--color-indigo-t11)">Please check the role that applies to you:</Text>
            <FormInputWrapper title="" name={FIELD.ROLE} newUi>
              <RadioGroup
                newUi
                name={FIELD.ROLE}
                options={[
                  {
                    value: ACCOUNT_ROLE.ADVISER,
                    text: `I am an adviser`,
                  },
                  {
                    value: ACCOUNT_ROLE.ADVISER_SUPPORT,
                    text: (
                      <>
                        I am an administrator
                        <br />
                        (I provide admin support to advisers)
                      </>
                    ),
                  },
                ]}
              />
            </FormInputWrapper>
          </Section>
        </BoxWrapper>

        <BoxWrapper wrapperStyles={{ marginTop: '20px' }}>
          <Heading mode="heading" weight="bold" style={{ marginBottom: '2rem' }}>
            How did you hear about us?
          </Heading>
          <Text color="var(--color-indigo-t11)">Please choose from the following options</Text>
          <FormInputWrapper title="" name={FIELD.HOW_DID_USER_HEAR_ABOUT_LIVEMORE} newUi>
            <RadioGroup
              newUi
              name={FIELD.HOW_DID_USER_HEAR_ABOUT_LIVEMORE}
              options={[
                {
                  value: USER_SOURCE_VALUE.GOOGLE_SEARCH,
                  text: USER_SOURCE_LABEL.GOOGLE_SEARCH,
                },
                {
                  value: USER_SOURCE_VALUE.WORD_OF_MOUTH,
                  text: USER_SOURCE_LABEL.WORD_OF_MOUTH,
                },
                {
                  value: USER_SOURCE_VALUE.SOCIAL_MEDIA,
                  text: USER_SOURCE_LABEL.SOCIAL_MEDIA,
                },
                {
                  value: USER_SOURCE_VALUE.PR,
                  text: USER_SOURCE_LABEL.PR,
                },
                {
                  value: USER_SOURCE_VALUE.COMMS_FROM_LIVEMORE,
                  text: USER_SOURCE_LABEL.COMMS_FROM_LIVEMORE,
                },
                {
                  value: USER_SOURCE_VALUE.COMMS_FROM_MY_NETWORK_PR,
                  text: USER_SOURCE_LABEL.COMMS_FROM_MY_NETWORK_PR,
                },
                {
                  value: USER_SOURCE_VALUE.SOURCING_SYSTEM_ADS,
                  text: USER_SOURCE_LABEL.SOURCING_SYSTEM_ADS,
                },
                {
                  value: USER_SOURCE_VALUE.WEBINAR,
                  text: USER_SOURCE_LABEL.WEBINAR,
                },
                {
                  value: USER_SOURCE_VALUE.ROADSHOW_EVENT,
                  text: USER_SOURCE_LABEL.ROADSHOW_EVENT,
                  provideMoreDetails: true,
                  fieldName: FIELD.OPTIONAL_DETAILS,
                },
                {
                  value: USER_SOURCE_VALUE.BUSINESS_DEVELOPMENT_CALL,
                  text: USER_SOURCE_LABEL.BUSINESS_DEVELOPMENT_CALL,
                  provideMoreDetails: true,
                  fieldName: FIELD.OPTIONAL_DETAILS,
                },
                {
                  value: USER_SOURCE_VALUE.NETWORK_LAUNCH,
                  text: USER_SOURCE_LABEL.NETWORK_LAUNCH,
                },
                {
                  value: USER_SOURCE_VALUE.OTHER,
                  text: USER_SOURCE_LABEL.OTHER,
                  provideMoreDetails: true,
                  fieldName: FIELD.OPTIONAL_DETAILS,
                },
              ]}
            />
          </FormInputWrapper>
          <RegisterErrorModal show={modalError !== null} error={modalError} onClose={() => setModalError(null)} />
          <Navigation onBack={handleBack} onContinue={handleContinue} continueDisabled={registerLoading} />
        </BoxWrapper>
      </form>
    </FormProvider>
  );
};

export default PersonalDetailsStep;
